import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { useAuth } from './useAuth';
import { Role } from '../types/auth';
import { openModal } from '../state/actions/ui/modal';
import { modalIds } from '../components/modals/SuperModal/types';
import { gpsPermissionIsDenied } from '../utils/gpsPermission';
import { getPositionSource } from '../utils/positionSource';

/**
 * Puerta de entrada al tramo: sin permiso de GPS no se abre uno.
 *
 * ── Qué problema resuelve ──────────────────────────────────────────────────
 *
 * El recorrido de vigilancia SIN GPS no existe: es el rastro lo que se
 * registra. Por eso ya había un modal bloqueante (`GPS_REQUIRED`) que tapa la
 * pantalla en cuanto el vigilante recibe un rechazo… solo que ese vigilante se
 * enciende con el recorrido YA en curso, o sea después de crear el tramo. El
 * operario pulsaba «Iniciar», rellenaba la ventana, creaba un tramo — y solo
 * entonces se le decía que sin GPS no se puede seguir, con un tramo abierto y
 * vacío ya guardado.
 *
 * Aquí se adelanta esa misma puerta al gesto que la provoca: si el permiso
 * está rechazado, «Iniciar» abre el bloqueante de siempre EN VEZ de la ventana
 * del tramo. Un solo modal, el mismo texto, y nada que deshacer después.
 *
 * ── Lo que NO hace ────────────────────────────────────────────────────────
 *
 * No comprueba si hay señal, ni pide una posición: eso tarda y falla en un
 * túnel o en el aparcamiento de la base, y bloquear ahí sería la cárcel sin
 * salida que ya se corrigió en julio (por eso el TIMEOUT no abre el modal).
 * Solo mira si el permiso está rechazado — ver `gpsPermissionIsDenied`.
 *
 * Y solo se aplica al rol **Tablet**, igual que el modal que abre (FR-14.1):
 * los demás roles ni siquiera pueden operar el cronómetro (`canOperateTimer`),
 * pero si algún día pudieran, mirar un mapa no debe exigirles GPS.
 */
export function useGpsGate() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const isTablet = (user?.user?.rolActive as Role | undefined) === Role.Tablet;

  /**
   * Ejecuta `abrirTramo` salvo que el GPS esté rechazado; en ese caso planta
   * el modal bloqueante. Su botón reintenta el permiso y, si esta vez sale
   * bien, sigue **por donde iba**: abre la ventana del tramo, para que el
   * operario no tenga que volver a buscar el botón.
   */
  const guardSegmentStart = useCallback(
    async (abrirTramo: () => void): Promise<void> => {
      if (!isTablet) {
        abrirTramo();
        return;
      }

      if (!(await gpsPermissionIsDenied())) {
        abrirTramo();
        return;
      }

      const pedirGps = (): Promise<'granted' | 'denied' | 'no-fix'> =>
        new Promise((resolve) => {
          const source = getPositionSource();
          if (!source) {
            resolve('no-fix');
            return;
          }
          source.getCurrent(
            () => {
              // El permiso ha vuelto: se retoma el gesto original. `openModal`
              // reemplaza al bloqueante (solo hay una ranura de modal), así que
              // no hace falta cerrarlo antes.
              abrirTramo();
              resolve('granted');
            },
            (err) => resolve(err.code === 1 ? 'denied' : 'no-fix')
          );
        });

      dispatch(openModal(modalIds.GPS_REQUIRED, { onRequestGps: pedirGps }));
    },
    [dispatch, isTablet]
  );

  return guardSegmentStart;
}

export default useGpsGate;
